// src/pages/History/BetHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

// ── Helpers ───────────────────────────────────────────────────────────────────
const toIST = (utcValue, withSeconds = false) => {
  if (!utcValue) return '-';
  const d = new Date(utcValue);
  if (isNaN(d)) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
    hour12: true,
  });
};

const toISTDateOnly = (utcValue) => {
  if (!utcValue) return '-';
  const d = new Date(utcValue);
  if (isNaN(d)) return '-';
  return d.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const toISTTimeOnly = (utcValue) => {
  if (!utcValue) return '-';
  const d = new Date(utcValue);
  if (isNaN(d)) return '-';
  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0';
  return Math.floor(amount).toString();
};

const GAME_LABELS = {
  classic:   'Royal King',
  roulette:  'Roulette',
  funtarget: 'Fun Target',
};

const getSuitIcon = (suit) => {
  switch (suit?.toLowerCase()) {
    case 'clubs':    return '/img/Clubs.png';
    case 'diamonds': return '/img/Diamonds.png';
    case 'hearts':   return '/img/Hearts.png';
    case 'spades':   return '/img/Spades.png';
    default:         return '';
  }
};

// "Q-Diamonds" → { value: "Q", suit: "Diamonds" }
const parseCard = (card) => {
  if (!card) return { value: '-', suit: null };
  const [value, suit] = card.split('-');
  return { value: value || card, suit: suit || null };
};

const getStatusBadgeClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'won':    return 'badge bg-success';
    case 'lost':   return 'badge bg-danger';
    default:       return 'badge bg-warning text-dark';
  }
};

// ── Component ─────────────────────────────────────────────────────────────────
const BetHistory = () => {
  const [loading, setLoading]         = useState(false);
  const [bets, setBets]               = useState([]);
  const [selectedBet, setSelectedBet] = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [searchTerm, setSearchTerm]   = useState('');   // client-side ticket/user search
  const [filters, setFilters]         = useState({
    gameType:  '',   // 'all' | 'classic' | 'roulette' | 'funtarget'
    userId:    '',
    search:    '',   // server-side username search
    startDate: '',
    endDate:   '',
  });
  const [pagination, setPagination] = useState({
    page: 1, limit: 10, total: 0, pages: 0,
  });

  // ── fetch ──────────────────────────────────────────────────────────────────
  const fetchBets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page:  pagination.page,
        limit: pagination.limit,
      };
      if (filters.gameType)  params.gameType  = filters.gameType;
      if (filters.userId)    params.userId    = filters.userId;
      if (filters.search)    params.search    = filters.search;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate)   params.endDate   = filters.endDate;

      const res = await api.get('/admin/bets/history', { params });
      if (res.data?.bets) {
        setBets(res.data.bets);
        setPagination(prev => ({
          ...prev,
          total: res.data.pagination?.total ?? 0,
          pages: res.data.pagination?.pages ?? 1,
        }));
      } else {
        setBets([]);
      }
    } catch (err) {
      console.error('Error fetching bets:', err);
      toast.error('Failed to fetch bet history');
      setBets([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => { fetchBets(); }, [fetchBets]);

  // ── filter helpers ─────────────────────────────────────────────────────────
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // debounced server search by username
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchBets();
    }, 500);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const clearFilters = () => {
    setFilters({ gameType: '', userId: '', search: '', startDate: '', endDate: '' });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // ── client-side ticket/username filter on visible rows ────────────────────
  const filteredBets = bets.filter(bet => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      bet.ticketId?.toLowerCase().includes(s) ||
      bet.username?.toLowerCase().includes(s)
    );
  });

  const startEntry = (pagination.page - 1) * pagination.limit + 1;
  const endEntry   = Math.min(pagination.page * pagination.limit, pagination.total);

  // ── modal ─────────────────────────────────────────────────────────────────
  const openModal  = (bet) => { setSelectedBet(bet); setShowModal(true); };
  const closeModal = ()    => { setShowModal(false); setSelectedBet(null); };

  // ── render bet items in modal depending on game type ─────────────────────
  const renderBetItems = (bet) => {
    const items = bet.numbers || bet.bets || [];
    if (!items.length) return <p className="text-muted">No bet details available.</p>;

    if (bet.game === 'classic') {
      return (
        <div className="row">
          {items.map((item, idx) => {
            const { value, suit } = parseCard(item.card);
            return (
              <div key={idx} className="col-md-3 col-sm-4 col-6 mb-3">
                <div className="card text-center bet-card-item">
                  <div className="card-body p-3">
                    <div className="result-circle d-flex align-items-center justify-content-center gap-2">
                      <span className="fw-bold fs-3">{value}</span>
                      {suit && (
                        <img src={getSuitIcon(suit)} alt={suit} style={{ width: 30, height: 30 }} />
                      )}
                    </div>
                    <div className="mt-2">
                      <span className="badge bg-primary">₹{item.amount}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
// ── FunTarget — items have { target, amount } ──
    if (bet.game === 'funtarget') {
      return (
        <div className="row">
          {items.map((item, idx) => (
            <div key={idx} className="col-md-2 col-sm-3 col-4 mb-3">
              <div className="card text-center bet-card-item h-100">
                <div className="card-body p-2 d-flex flex-column align-items-center justify-content-between">
                  <span className="fw-bold fs-3 my-1">{item.target ?? '-'}</span>
                  <span className="badge bg-primary mt-1">₹{item.amount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }
    // roulette / funtarget — items have { betType, value: ["n,n,n,"], amount }
    // Parse the value string: "4," → "4"  |  "14,15,16," → "14,15,16"
    const parseValue = (raw) => {
      if (!raw) return '-';
      const str = Array.isArray(raw) ? raw[0] : raw;
      return str.replace(/,\s*$/, '').trim() || '-';
    };

    // Human-readable label for betType
    const BET_TYPE_LABELS = {
      straight:    'Straight',
      split:       'Split',
      street:      'Street',
      corner:      'Corner',
      line:        'Line',
      firstDozen:  '1st Dozen',
      secondDozen: '2nd Dozen',
      thirdDozen:  '3rd Dozen',
      firstRow:    '1st Row',
      secondRow:   '2nd Row',
      thirdRow:    '3rd Row',
      red:         'Red',
      black:       'Black',
      odd:         'Odd',
      even:        'Even',
      low:         'Low (1-18)',
      high:        'High (19-36)',
    };

    // For straight bets show just the number big; for others show label + numbers small
    return (
      <div className="row">
        {items.map((item, idx) => {
          const valStr   = parseValue(item.value);
          const label    = BET_TYPE_LABELS[item.betType] || item.betType || '';
          const isStraight = item.betType === 'straight';
          return (
            <div key={idx} className={`${isStraight ? 'col-md-2 col-sm-3 col-4' : 'col-md-3 col-sm-4 col-6'} mb-3`}>
              <div className="card text-center bet-card-item h-100">
                <div className="card-body p-2 d-flex flex-column align-items-center justify-content-between">
                  {isStraight ? (
                    <span className="fw-bold fs-3 my-1">{valStr}</span>
                  ) : (
                    <>
                      <span className="badge bg-secondary mb-1" style={{ fontSize: '0.7rem' }}>{label}</span>
                      <span className="fw-semibold" style={{ fontSize: '0.78rem', wordBreak: 'break-all', color: '#555' }}>
                        {valStr}
                      </span>
                    </>
                  )}
                  <span className="badge bg-primary mt-1">₹{item.amount}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ── result display in modal ───────────────────────────────────────────────
  const renderResult = (bet) => {
    if (bet.result === null || bet.result === undefined) return null;

    if (bet.game === 'classic') {
      const { value, suit } = parseCard(bet.result);
      return (
        <div className="mt-4 alert alert-info">
          <h6 className="mb-2">Draw Result</h6>
          <div className="d-flex align-items-center gap-3">
            <div className="result-circle d-flex align-items-center justify-content-center gap-2">
              <span className="fw-bold fs-2">{value}</span>
              {suit && <img src={getSuitIcon(suit)} alt={suit} style={{ width: 40, height: 40 }} />}
            </div>
          </div>
        </div>
      );
    }

    // roulette / funtarget: result is a plain number
    const num = typeof bet.result === 'object'
      ? (bet.result?.number ?? bet.result?.target ?? bet.result?.result ?? '-')
      : bet.result;

    return (
      <div className="mt-4 alert alert-info d-flex align-items-center gap-3">
        <div>
          <h6 className="mb-1">Draw Result</h6>
          <span className="fw-bold fs-2">{num}</span>
        </div>
        <span className={`badge fs-6 ${bet.status === 'won' ? 'bg-success' : 'bg-danger'}`}>
          {bet.status}
        </span>
      </div>
    );
  };

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="bet-history-page">
      <div className="container-fluid">
        <div className="page-hedding-header">
          <h1 className="page-heading h3 text-uppercase fs-4">Bet History</h1>
        </div>

        <div className="admin-card-one">
          <div className="admin-card-header">
            <h2 className="admin-card-heading">Bet History</h2>
          </div>
          <div className="admin-card-body">

            {/* ── Filters ── */}
            <div className="row g-3 pb-3 align-items-end">
              {/* Game Type */}
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">Select Game</label>
                <select
                  name="gameType"
                  value={filters.gameType}
                  onChange={handleFilterChange}
                  className="form-select admin-select-one"
                >
                  <option value="">All Games</option>
                  <option value="classic">RoyalKing</option>
                  <option value="roulette">Roulette</option>
                  <option value="funtarget">Fun Target</option>
                </select>
              </div>

              {/* Server username search */}
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">Search User</label>
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="form-control admin-input-one"
                  placeholder="Search by username…"
                />
              </div>

              {/* Date range */}
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">From Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="form-control admin-input-one"
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="admin-label-one">To Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="form-control admin-input-one"
                />
              </div>
            </div>

            <div className="row g-3 pb-3">
              <div className="col-12 text-end">
                <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>

            {/* ── Table controls ── */}
            <div className="admin-table-top">
              <div className="entries-box">
                <label>Show</label>
                <select
                  value={pagination.limit}
                  onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
              <div className="search-box">
                <label>Search:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Filter by ticket / username…"
                />
              </div>
            </div>

            {/* ── Table ── */}
            <div className="table-responsive">
              <table className="table table-admin-one">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Create Date (IST)</th>
                    <th>Draw Time (IST)</th>
                    <th>Username</th>
                    <th>Game Type</th>
                    <th>Ticket ID</th>
                    <th>Start Point</th>
                    <th>Play Point</th>
                    <th>Won Point</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading…</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredBets.length > 0 ? (
                    filteredBets.map((bet, index) => (
                      <tr
                        key={bet._id || index}
                        onClick={() => openModal(bet)}
                        style={{ cursor: 'pointer' }}
                        className="bet-row"
                      >
                        <td>{startEntry + index}</td>
                        <td>{toISTDateOnly(bet.placedAt || bet.createdAt)}</td>
                        <td>{toISTTimeOnly(bet.drawTime)}</td>
                        <td>{bet.username || '-'}</td>
                        <td>
                          <span className="badge bg-secondary">
                            {GAME_LABELS[bet.game] || bet.game || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="ticket-id">
                            {bet.ticketId || `#TK${bet._id?.slice(-6)}`}
                          </span>
                        </td>
                        <td>{formatCurrency(bet.startPoint)}</td>
                        <td>{formatCurrency(bet.points)}</td>
                        <td className={bet.status === 'won' ? 'win' : bet.status === 'lost' ? 'loss' : ''}>
                          {bet.status === 'won' ? formatCurrency(bet.winnings) : '0'}
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(bet.status)}>
                            {bet.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center py-4">No bets found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Pagination ── */}
            {pagination.pages > 0 && (
              <div className="admin-table-bottom">
                <div className="table-info">
                  Showing {startEntry} to {endEntry} of {pagination.total} entries
                </div>
                <div className="table-pagination">
                  <button
                    className="page-btn prev"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                  >
                    Prev
                  </button>
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    let p;
                    if (pagination.pages <= 5)              p = i + 1;
                    else if (pagination.page <= 3)          p = i + 1;
                    else if (pagination.page >= pagination.pages - 2) p = pagination.pages - 4 + i;
                    else                                    p = pagination.page - 2 + i;
                    return (
                      <button
                        key={p}
                        className={`page-btn ${pagination.page === p ? 'active' : ''}`}
                        onClick={() => setPagination(prev => ({ ...prev, page: p }))}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    className="page-btn next"
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bet Detail Modal ── */}
      {showModal && selectedBet && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                <h5 className="modal-title">Bet Details — {selectedBet.ticketId}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
              </div>
              <div className="modal-body">

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-2 text-muted">Bet Information</h6>
                        <p className="mb-1"><strong>Username:</strong> {selectedBet.username}</p>
                        <p className="mb-1"><strong>Full Name:</strong> {selectedBet.fullName || '-'}</p>
                        <p className="mb-1"><strong>Game:</strong> {GAME_LABELS[selectedBet.game] || selectedBet.game}</p>
                        <p className="mb-1"><strong>Draw #:</strong> {selectedBet.drawNumber || '-'}</p>
                        <p className="mb-1"><strong>Draw Time (IST):</strong> {toIST(selectedBet.drawTime)}</p>
                        <p className="mb-1"><strong>Bet Placed (IST):</strong> {toIST(selectedBet.placedAt)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card bg-light">
                      <div className="card-body">
                        <h6 className="card-subtitle mb-2 text-muted">Financial Details</h6>
                        <p className="mb-1"><strong>Start Point:</strong> {formatCurrency(selectedBet.startPoint)}</p>
                        <p className="mb-1"><strong>Play Point:</strong> {formatCurrency(selectedBet.points)}</p>
                        <p className="mb-1">
                          <strong>Won Point:</strong>{' '}
                          <span className={selectedBet.status === 'won' ? 'text-success fw-bold' : ''}>
                            {formatCurrency(selectedBet.winnings)}
                          </span>
                        </p>
                        <p className="mb-1">
                          <strong>Balance After:</strong>{' '}
                          {formatCurrency(selectedBet.balanceAfter)}
                        </p>
                        <p className="mb-1">
                          <strong>Status:</strong>{' '}
                          <span className={getStatusBadgeClass(selectedBet.status)}>
                            {selectedBet.status || 'pending'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bet items */}
                <h6 className="mb-3">
                  {selectedBet.game === 'classic' ? 'Cards Bet On' : 'Numbers Bet On'}
                </h6>
                {renderBetItems(selectedBet)}

                {/* Result */}
                {renderResult(selectedBet)}

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BetHistory;